import { c as _c } from "react/compiler-runtime";
import React, { FunctionComponent, ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { useEventCallback } from 'record-it-components/hooks/useEventCallback';
import { prepareProps } from 'record-it-components/utils/propUtils';
import { PropsWithoutChildren } from 'record-it-components/utils/utilityTypes';
import { WorkTimeEntryBreak } from 'record-it-time-common/entity/entities/WorkTimeEntry/WorkTimeEntryDto';
import { CreateTimeEntryBreakInline, CreateTimeEntryBreakInlineProps } from '../CreateTimeEntryBreakInline/CreateTimeEntryBreakInline';
import { WorkTimeEntryBreakListItem, WorkTimeEntryBreakListItemProps } from '../WorkTimeEntryBreakListItem/WorkTimeEntryBreakListItem';
import styles from './EditWorkTimeEntryBreaksWidget.module.css';
export type EditWorkTimeEntryBreaksWidgetProps = PropsWithoutChildren<ComponentProps<'div'>> & {
  /**
   * Start date a new break is initially set to.
   */
  newBreakInitialStartDate?: Date;
  /**
   * End date a new break is initially set to.
   */
  newBreakInitialEndDate?: Date;
  workTimeEntryBreaks: Array<WorkTimeEntryBreak>;
  onWorkTimeEntryBreaksModified: (breaks: Array<WorkTimeEntryBreak>) => void;
};
export const EditWorkTimeEntryBreaksWidget: FunctionComponent<EditWorkTimeEntryBreaksWidgetProps> = React.memo(props => {
  const $ = _c(36);
  let t0;
  if ($[0] !== props) {
    t0 = prepareProps({
      props,
      ignoreChildren: true
    });
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let className;
  let newBreakInitialEndDate;
  let newBreakInitialStartDate;
  let onWorkTimeEntryBreaksModified;
  let remainingProps;
  let workTimeEntryBreaks;
  if ($[2] !== t0) {
    ({
      workTimeEntryBreaks,
      onWorkTimeEntryBreaksModified,
      className,
      newBreakInitialStartDate,
      newBreakInitialEndDate,
      ...remainingProps
    } = t0);
    $[2] = t0;
    $[3] = className;
    $[4] = newBreakInitialEndDate;
    $[5] = newBreakInitialStartDate;
    $[6] = onWorkTimeEntryBreaksModified;
    $[7] = remainingProps;
    $[8] = workTimeEntryBreaks;
  } else {
    className = $[3];
    newBreakInitialEndDate = $[4];
    newBreakInitialStartDate = $[5];
    onWorkTimeEntryBreaksModified = $[6];
    remainingProps = $[7];
    workTimeEntryBreaks = $[8];
  }
  const {
    t
  } = useTranslation();
  let t1;
  if ($[9] !== onWorkTimeEntryBreaksModified || $[10] !== workTimeEntryBreaks) {
    t1 = toCreate => {
      const lastIndex = workTimeEntryBreaks[workTimeEntryBreaks.length - 1]?.index ?? -1;
      onWorkTimeEntryBreaksModified([...workTimeEntryBreaks, {
        index: lastIndex + 1,
        startDate: toCreate.startDate,
        endDate: toCreate.endDate
      }]);
    };
    $[9] = onWorkTimeEntryBreaksModified;
    $[10] = workTimeEntryBreaks;
    $[11] = t1;
  } else {
    t1 = $[11];
  }
  const handleCreateTimeEntry = useEventCallback(t1);
  let t2;
  if ($[12] !== onWorkTimeEntryBreaksModified || $[13] !== workTimeEntryBreaks) {
    t2 = timeEntryBreak => {
      const newBreaks = workTimeEntryBreaks.filter(b => b !== timeEntryBreak).map(_temp);
      onWorkTimeEntryBreaksModified(newBreaks);
    };
    $[12] = onWorkTimeEntryBreaksModified;
    $[13] = workTimeEntryBreaks;
    $[14] = t2;
  } else {
    t2 = $[14];
  }
  const handleDeleteTimeEntry = useEventCallback(t2);
  let t3;
  if ($[15] !== t) {
    t3 = t("components.timeTracking.EditWorkTimeEntryBreaksWidget.title");
    $[15] = t;
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  let t4;
  if ($[17] !== t3) {
    t4 = <div className={styles.title}>{t3}</div>;
    $[17] = t3;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  let t5;
  if ($[19] !== handleDeleteTimeEntry || $[20] !== workTimeEntryBreaks) {
    let t6;
    if ($[22] !== handleDeleteTimeEntry) {
      t6 = b_1 => <WorkTimeEntryBreakListItem key={b_1.index} workTimeEntryBreak={b_1} onDelete={handleDeleteTimeEntry} data-testid="EditWorkTimeEntryBreaksWidget.BreakListItem" />;
      $[22] = handleDeleteTimeEntry;
      $[23] = t6;
    } else {
      t6 = $[23];
    }
    t5 = workTimeEntryBreaks.map(t6);
    $[19] = handleDeleteTimeEntry;
    $[20] = workTimeEntryBreaks;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  let t6;
  if ($[24] !== t5) {
    t6 = <div className={styles.list}>{t5}</div>;
    $[24] = t5;
    $[25] = t6;
  } else {
    t6 = $[25];
  }
  let t7;
  if ($[26] !== handleCreateTimeEntry || $[27] !== newBreakInitialEndDate || $[28] !== newBreakInitialStartDate) {
    t7 = <CreateTimeEntryBreakInline onCreate={handleCreateTimeEntry} initialStartDate={newBreakInitialStartDate} initialEndDate={newBreakInitialEndDate} data-testid="EditWorkTimeEntryBreaksWidget.CreateTimeEntryBreakInline" />;
    $[26] = handleCreateTimeEntry;
    $[27] = newBreakInitialEndDate;
    $[28] = newBreakInitialStartDate;
    $[29] = t7;
  } else {
    t7 = $[29];
  }
  let t8;
  if ($[30] !== className || $[31] !== remainingProps || $[32] !== t4 || $[33] !== t6 || $[34] !== t7) {
    t8 = <div {...remainingProps} className={className}>{t4}{t6}{t7}</div>;
    $[30] = className;
    $[31] = remainingProps;
    $[32] = t4;
    $[33] = t6;
    $[34] = t7;
    $[35] = t8;
  } else {
    t8 = $[35];
  }
  return t8;
});
function _temp(b_0, index) {
  if (b_0.index === index) {
    return b_0;
  }
  return {
    ...b_0,
    index
  };
}